import { z } from 'zod';
import type {
  BalanceSheetResponse,
  CashFlowResponse,
  IncomeStatementResponse,
} from './report.schemas.js';

/**
 * Read-only financial export for a machine caller.
 *
 * Built for the Carries bar inventory app (same Entra tenant), which pulls one
 * company's monthly income statement and balance sheet with an app-only token
 * carrying the Financials.Read role. The figures come from the same report
 * builders the UI uses, so this surface can never disagree with the screens.
 * Money is strings, dates are YYYY-MM-DD, months are YYYY-MM.
 */

export const monthQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'expected YYYY-MM')
    .refine(
      (m) => {
        const mm = Number(m.slice(5, 7));
        return mm >= 1 && mm <= 12;
      },
      { message: 'month must be 01 through 12' },
    ),
});
export type MonthQuery = z.infer<typeof monthQuerySchema>;

/** One calendar month in a company's history and whether its books are locked. */
export interface ExportPeriodMonth {
  /** YYYY-MM */
  month: string;
  /** First calendar day of the month, YYYY-MM-DD. */
  periodFrom: string;
  /** Last calendar day of the month, YYYY-MM-DD. */
  periodTo: string;
  /**
   * True when the company's period lock covers the whole month
   * (periodLockedThrough >= periodTo). A locked month's figures are final.
   */
  locked: boolean;
}

export interface ExportPeriodStatusResponse {
  companyId: string;
  companyName: string;
  /** 1 through 12. */
  fiscalYearStartMonth: number;
  /** 'accrual' or 'cash'. */
  accountingBasis: string;
  /** YYYY-MM-DD, or null when no period has been locked. */
  periodLockedThrough: string | null;
  /** Highest fiscal year formally closed, or null when none has been. */
  fiscalYearClosedThrough: number | null;
  /**
   * Every month from the company's earliest non-void journal entry (or the
   * current month when there are none) through the current month, ascending.
   */
  months: ExportPeriodMonth[];
}

export interface ExportMonthlyFinancialsResponse {
  companyId: string;
  companyName: string;
  /** YYYY-MM */
  month: string;
  periodFrom: string;
  periodTo: string;
  /** First day of the fiscal year containing periodTo, YYYY-MM-DD. */
  fiscalYearStart: string;
  /** Same rule as ExportPeriodMonth.locked. */
  locked: boolean;
  periodLockedThrough: string | null;
  /** The month alone: periodFrom through periodTo. */
  incomeStatement: IncomeStatementResponse;
  /** Fiscal year to date: fiscalYearStart through periodTo. */
  incomeStatementYtd: IncomeStatementResponse;
  /** As of periodTo. */
  balanceSheet: BalanceSheetResponse;
  /** Indirect cash flow for the month alone: periodFrom through periodTo. */
  cashFlow: CashFlowResponse;
  /** Indirect cash flow, fiscal year to date: fiscalYearStart through periodTo. */
  cashFlowYtd: CashFlowResponse;
  /** ISO 8601 datetime the response was computed. */
  generatedAt: string;
}

// ── Liquor cost of goods sold posting ────────────────────────────────────────
//
// The inventory app closes a weekly count and computes liquor cost of goods
// sold (beginning value plus purchases minus ending count value). It pushes
// that one figure here with an app-only token carrying Financials.Post, and it
// lands as a journal entry dated the count date: debit liquor cost of goods,
// credit liquor inventory. Purchases already reach the inventory asset through
// the bank feed, so this entry is what turns the asset balance into the counted
// value. One posting per inventory period, keyed by sourceRef.

const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

/** Signed money string with exactly two decimals, for example "1234.56" or "-12.00". */
const MONEY_2DP = z
  .string()
  .regex(/^-?\d+\.\d{2}$/, 'must be a money string with exactly two decimals');

export const liquorCogsPostSchema = z.object({
  /**
   * The caller's stable key for the inventory period, for example
   * "inventory-app:period:146". Repeating a post with the same sourceRef
   * updates the earlier entry instead of adding a second one.
   */
  sourceRef: z.string().min(1).max(120),
  /** The count date, YYYY-MM-DD. The journal entry carries this date. */
  date: ISO_DATE,
  /**
   * Cost of goods sold for the period. Negative when the count came in higher
   * than beginning plus purchases (the entry is posted in reverse). Zero
   * removes any earlier entry for the period and posts nothing.
   */
  amount: MONEY_2DP,
  memo: z.string().max(200).optional(),
});
export type LiquorCogsPostInput = z.infer<typeof liquorCogsPostSchema>;

export const liquorCogsListQuerySchema = z
  .object({
    /** Inclusive lower bound on the entry date, YYYY-MM-DD. */
    from: ISO_DATE.optional(),
    /** Inclusive upper bound on the entry date, YYYY-MM-DD. */
    to: ISO_DATE.optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'from must not be after to',
    path: ['from'],
  });
export type LiquorCogsListQuery = z.infer<typeof liquorCogsListQuerySchema>;

/**
 * What happened to the books:
 *  - created:   no entry existed for sourceRef; one was posted.
 *  - replaced:  an entry existed and differed (date or amount); it was voided
 *               and, unless the new amount is zero, a new entry was posted.
 *  - unchanged: an entry existed with the same date and amount; nothing written.
 *  - none:      amount is zero and no entry existed; nothing written.
 */
export type LiquorCogsPostAction = 'created' | 'replaced' | 'unchanged' | 'none';

export interface LiquorCogsPostResponse {
  sourceRef: string;
  /** YYYY-MM-DD, as posted. */
  date: string;
  /** Signed, two decimals, as posted. */
  amount: string;
  /** The live journal entry for this sourceRef after the call, or null when there is none. */
  entryId: string | null;
  /** The entry that was voided by this call, or null when none was. */
  previousEntryId: string | null;
  action: LiquorCogsPostAction;
}

/** One live (non-void) entry the sync has posted. */
export interface LiquorCogsPosting {
  sourceRef: string;
  /** YYYY-MM-DD */
  date: string;
  /** Signed, two decimals: positive when cost of goods was debited. */
  amount: string;
  entryId: string;
  /** ISO 8601 datetime the entry was written. */
  createdAt: string;
}

// ── Daily sales posting ──────────────────────────────────────────────────────
//
// The inventory app also holds the bar's Square figures. Once a day it sends
// one calendar day's sales, tips, taxes, receipts and processing fees, and
// they land as a single journal entry dated that day. Revenue, tips and taxes
// are then recognised from Square's own numbers instead of being inferred from
// the bank deposit; the Square payout later clears undeposited funds. One
// posting per day, keyed by sourceRef, replaced by void and create exactly
// like the liquor cost posting above.
//
// Every figure is signed. A refund day can leave any of them below zero (a
// card refund of yesterday's tab with no card sales today gives negative card
// receipts and negative sales tax), and a negative figure posts its absolute
// value on the opposite side of the account it names.
//
// Optionally the day's net sales come split three ways: liquor, food and
// other. The three are sent together or not at all, and when they are sent
// they must add up to netSales exactly; the entry then credits three revenue
// accounts instead of the one sales account. A body without the split posts
// exactly as before. The inventory app re-posts past days with the split to
// move their revenue onto the three accounts.

/** The three optional revenue split fields, in the order the entry lists them. */
export const DAILY_SALES_SPLIT_FIELDS = ['liquorSales', 'foodSales', 'otherSales'] as const;
export type DailySalesSplitField = (typeof DAILY_SALES_SPLIT_FIELDS)[number];

const dailySalesPostObjectSchema = z.object({
  /**
   * The caller's stable key for the day, for example
   * "inventory-app:sales:2026-09-01". Repeating a post with the same sourceRef
   * updates the earlier entry instead of adding a second one.
   */
  sourceRef: z.string().min(1).max(120),
  /** The sales day, YYYY-MM-DD. The journal entry carries this date. */
  date: ISO_DATE,
  /**
   * Net sales for the day (before tips and taxes). Credited to sales; a
   * negative is debited. When the split below is sent, this is the total the
   * three split figures must add up to, and the sales account is not touched.
   */
  netSales: MONEY_2DP,
  /**
   * Optional revenue split, all three or none. Liquor sales for the day,
   * credited to liquor sales; a negative is debited.
   */
  liquorSales: MONEY_2DP.optional(),
  /** Food sales for the day. Credited to food sales; a negative is debited. Sent with the other two or not at all. */
  foodSales: MONEY_2DP.optional(),
  /** Everything else sold for the day. Credited to other sales; a negative is debited. Sent with the other two or not at all. */
  otherSales: MONEY_2DP.optional(),
  /** Kansas liquor drink tax collected. Credited to drink tax payable; a negative is debited. */
  drinkTax: MONEY_2DP,
  /** Retail sales tax collected. Credited to sales tax payable; a negative is debited. */
  salesTax: MONEY_2DP,
  /** Tips taken on cards, owed to staff. Credited to tips payable; a negative is debited. */
  cardTips: MONEY_2DP,
  /** Gross card takings for the day. Debited to undeposited funds; a negative is credited. */
  cardReceipts: MONEY_2DP,
  /** Cash taken for the day. Debited to cash on hand; a negative is credited. */
  cashReceipts: MONEY_2DP,
  /** Card processing fees for the day. Debited to merchant fees; a negative is credited. */
  processingFees: MONEY_2DP,
  memo: z.string().max(200).optional(),
});

/** The split fields missing from a body that carries at least one of them; empty for all or none. */
export function missingDailySalesSplitFields(
  input: Partial<Record<DailySalesSplitField, unknown>>,
): DailySalesSplitField[] {
  const present = DAILY_SALES_SPLIT_FIELDS.filter((field) => input[field] !== undefined);
  if (present.length === 0 || present.length === DAILY_SALES_SPLIT_FIELDS.length) return [];
  return DAILY_SALES_SPLIT_FIELDS.filter((field) => input[field] === undefined);
}

export const dailySalesPostSchema = dailySalesPostObjectSchema.superRefine((body, ctx) => {
  // The split is all or none: one issue per missing field, so the caller sees
  // every one it left out at once. The sum rule lives in the service, which
  // reports both sums the way the balance rule does.
  for (const field of missingDailySalesSplitFields(body)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [field],
      message: `${field} is missing: liquorSales, foodSales and otherSales are sent together or not at all`,
    });
  }
});
/**
 * One day's figures as the route accepts them. The three split fields are
 * either all present or all absent; when present they add up to netSales.
 */
export type DailySalesPostInput = z.infer<typeof dailySalesPostSchema>;

/** True when a body carries the three split fields (the schema has already made them all or none). */
export function hasDailySalesSplit(
  input: DailySalesPostInput,
): input is DailySalesPostInput & Record<DailySalesSplitField, string> {
  return DAILY_SALES_SPLIT_FIELDS.every((field) => input[field] !== undefined);
}

/**
 * The seven money fields every daily sales posting carries, in the order the
 * entry lists them: the three receipt-side figures (debits when positive),
 * then the four recognised figures (credits when positive). The optional
 * split fields are listed separately in DAILY_SALES_SPLIT_FIELDS.
 */
export const DAILY_SALES_FIELDS = [
  'cardReceipts',
  'cashReceipts',
  'processingFees',
  'netSales',
  'cardTips',
  'drinkTax',
  'salesTax',
] as const;
export type DailySalesField = (typeof DAILY_SALES_FIELDS)[number];

/** Same shape and rules as liquorCogsListQuerySchema: an inclusive date range on the entry date. */
export const dailySalesListQuerySchema = liquorCogsListQuerySchema;
export type DailySalesListQuery = z.infer<typeof dailySalesListQuerySchema>;

/** Same meanings as LiquorCogsPostAction. */
export type DailySalesPostAction = LiquorCogsPostAction;

export interface DailySalesPostResponse {
  sourceRef: string;
  /** YYYY-MM-DD, as posted. */
  date: string;
  /** The live journal entry for this sourceRef after the call, or null when there is none. */
  entryId: string | null;
  /** The entry that was voided by this call, or null when none was. */
  previousEntryId: string | null;
  action: DailySalesPostAction;
  /**
   * Entry totals, two decimals, summed over the actual sides of the lines
   * written (a negative figure sits on the opposite side). Equal by
   * construction; both "0.00" when nothing is live.
   */
  totals: { debits: string; credits: string };
}

/**
 * One live (non-void) daily sales entry, reconstructed from its lines by
 * account. Each figure is signed the way it was sent: a credit on undeposited
 * funds reads as negative cardReceipts, a debit on sales tax payable as
 * negative salesTax, and so on.
 */
export interface DailySalesPosting {
  sourceRef: string;
  /** YYYY-MM-DD */
  date: string;
  entryId: string;
  /** ISO 8601 datetime the entry was written. */
  createdAt: string;
  /**
   * The credit on the sales account plus the credits on the three split
   * accounts, so an entry posted before the split and one posted with it
   * both read the day's whole net sales.
   */
  netSales: string;
  /** Credit on the liquor sales account; "0.00" when the entry has no line there or the account is not configured. */
  liquorSales: string;
  /** Credit on the food sales account; "0.00" when the entry has no line there or the account is not configured. */
  foodSales: string;
  /** Credit on the other sales account; "0.00" when the entry has no line there or the account is not configured. */
  otherSales: string;
  cardTips: string;
  drinkTax: string;
  salesTax: string;
  cardReceipts: string;
  cashReceipts: string;
  processingFees: string;
}

// ── Payroll tips export ──────────────────────────────────────────────────────
//
// The inventory app shows whose tips Square collected, month by month. To set
// that against what payroll actually paid out, it reads the tips components
// of every posted paystub here: card tips paid on the check
// (gross_wages_tips) and cash tips the employee already had and only declared
// for tax (gross_wages_tips_cash). Read only; nothing is computed beyond
// summing the stored components. A stub belongs to the calendar month of its
// pay date.

/** A calendar month, YYYY-MM with a real month number. Same rule as monthQuerySchema. */
const MONTH = monthQuerySchema.shape.month;

export const payrollTipsQuerySchema = z
  .object({
    /** First month included, YYYY-MM. */
    from: MONTH,
    /** Last month included, YYYY-MM. */
    to: MONTH,
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] });
export type PayrollTipsQuery = z.infer<typeof payrollTipsQuerySchema>;

/** One person's tips over some span, two-decimal strings. */
export interface ExportPayrollTipsPerson {
  /**
   * Stable identity for the person: the employee contact id when the stub
   * carries one, otherwise "name:" followed by the normalised employee name.
   * The same person keeps the same key across months.
   */
  employeeKey: string;
  /** The display name from the most recent stub in the span. */
  name: string;
  /** Sum of gross_wages_tips: card tips paid to the person on the check. */
  cardTipsPaid: string;
  /** Sum of gross_wages_tips_cash: cash tips declared for tax, already in hand. */
  cashTipsDeclared: string;
}

export interface ExportPayrollTipsMonthPerson extends ExportPayrollTipsPerson {
  /** Distinct pay dates in the month that carried a tips component, YYYY-MM-DD ascending. */
  payDates: string[];
}

export interface ExportPayrollTipsMonth {
  /** YYYY-MM */
  month: string;
  /** Everyone with at least one tips component on a stub paid in the month, by name. */
  people: ExportPayrollTipsMonthPerson[];
}

export interface ExportPayrollTipsResponse {
  /** YYYY-MM, as requested. */
  from: string;
  /** YYYY-MM, as requested. */
  to: string;
  /** Every month from `from` through `to`, ascending; a month with no tips has an empty `people`. */
  months: ExportPayrollTipsMonth[];
  /** The same people summed over the whole span, by name. */
  totals: { people: ExportPayrollTipsPerson[] };
}

// ── Payroll tips by stub (from the ledger) ───────────────────────────────────
//
// The component sums above can overstate: a register imported twice leaves
// extra stubs pointing at the same live journal entries as the real ones, and
// summing components counts each of those entries as many times as there are
// stubs on it. The ledger is right, so this surface reads the figure from the
// ledger instead: for every live payroll journal entry, the net debit on the
// tips payable account. Every card tip paid through payroll sits there (older
// entries were broken out onto it; newer ones post there directly), and an
// entry is one entry no matter how many stubs point at it.

/** Real calendar day for a YYYY-MM-DD string, as days since the epoch, or null when it is not one. */
export function isoDateToDayNumber(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  const ms = Date.UTC(y, m - 1, d);
  const round = new Date(ms);
  if (round.getUTCFullYear() !== y || round.getUTCMonth() !== m - 1 || round.getUTCDate() !== d) {
    return null;
  }
  return Math.floor(ms / 86_400_000);
}

/** Longest span this export serves, in days, counting both ends. */
export const PAYROLL_TIPS_BY_STUB_MAX_DAYS = 400;

const CALENDAR_DATE = ISO_DATE.refine((v) => isoDateToDayNumber(v) !== null, {
  message: 'must be a real calendar date',
});

export const payrollTipsByStubQuerySchema = z
  .object({
    /** First pay date included, YYYY-MM-DD. */
    from: CALENDAR_DATE,
    /** Last pay date included, YYYY-MM-DD. */
    to: CALENDAR_DATE,
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine(
    (q) => {
      const a = isoDateToDayNumber(q.from);
      const b = isoDateToDayNumber(q.to);
      return a !== null && b !== null && b - a + 1 <= PAYROLL_TIPS_BY_STUB_MAX_DAYS;
    },
    { message: `range must be at most ${PAYROLL_TIPS_BY_STUB_MAX_DAYS} days`, path: ['to'] },
  );
export type PayrollTipsByStubQuery = z.infer<typeof payrollTipsByStubQuerySchema>;

/**
 * One live payroll journal entry's card tips, attributed to one person.
 * An entry linked from a paystub is one row. An older run-level entry that
 * covers several people is one row per person, all sharing the entryId, with
 * the entry's figure split in proportion to the stubs' own gross_wages_tips
 * components.
 */
export interface ExportPayrollTipsStub {
  entryId: string;
  entryNumber: string;
  /** YYYY-MM-DD: the stub's pay date, or the run's for a run-level entry. */
  payDate: string;
  /** Same rule as ExportPayrollTipsPerson.employeeKey. */
  employeeKey: string;
  name: string;
  /**
   * Net debit on the tips payable account in this entry (debits minus
   * credits), two decimals, signed.
   */
  cardTipsPaid: string;
  /** Which link found the entry: the stub's own journal_entry_id, or the run's. */
  source: 'stub' | 'run';
}

export interface ExportPayrollTipsByStubPerson {
  employeeKey: string;
  name: string;
  cardTipsPaid: string;
}

export interface ExportPayrollTipsByStubResponse {
  /** YYYY-MM-DD, as requested. */
  from: string;
  /** YYYY-MM-DD, as requested. */
  to: string;
  /** The account code the figure was read from. */
  tipsAccountCode: string;
  /** Sorted by pay date, then name, then entry number. */
  stubs: ExportPayrollTipsStub[];
  /** The same rows summed by person over the whole span, by name. */
  totals: { people: ExportPayrollTipsByStubPerson[] };
}

// ── Period financials (any date range) ───────────────────────────────────────
//
// The monthly export above answers for a calendar month. The inventory app's
// data room also shows the books by week, and a week rarely sits inside one
// month, so this surface takes the two ends of the range instead. Everything
// else is the monthly export: the same report builders, the same shapes, the
// same year to date statements beside the period's own. Read only.

/** Longest span this export serves, in days, counting both ends: a quarter. */
export const PERIOD_FINANCIALS_MAX_DAYS = 92;

export const periodFinancialsQuerySchema = z
  .object({
    /** First day included, YYYY-MM-DD. */
    from: CALENDAR_DATE,
    /** Last day included, YYYY-MM-DD. */
    to: CALENDAR_DATE,
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine(
    (q) => {
      const a = isoDateToDayNumber(q.from);
      const b = isoDateToDayNumber(q.to);
      return a !== null && b !== null && b - a + 1 <= PERIOD_FINANCIALS_MAX_DAYS;
    },
    { message: `range must be at most ${PERIOD_FINANCIALS_MAX_DAYS} days`, path: ['to'] },
  );
export type PeriodFinancialsQuery = z.infer<typeof periodFinancialsQuerySchema>;

/** ExportMonthlyFinancialsResponse for an arbitrary range: the same fields, less `month`. */
export interface ExportPeriodFinancialsResponse {
  companyId: string;
  companyName: string;
  /** YYYY-MM-DD, as requested. */
  periodFrom: string;
  /** YYYY-MM-DD, as requested. */
  periodTo: string;
  /** First day of the fiscal year containing periodTo, YYYY-MM-DD. */
  fiscalYearStart: string;
  /**
   * True when the company's period lock covers the whole range
   * (periodLockedThrough >= periodTo). A locked range's figures are final.
   */
  locked: boolean;
  periodLockedThrough: string | null;
  /** The range alone: periodFrom through periodTo. */
  incomeStatement: IncomeStatementResponse;
  /** Fiscal year to date: fiscalYearStart through periodTo. */
  incomeStatementYtd: IncomeStatementResponse;
  /** As of periodTo. */
  balanceSheet: BalanceSheetResponse;
  /** Indirect cash flow for the range alone: periodFrom through periodTo. */
  cashFlow: CashFlowResponse;
  /** Indirect cash flow, fiscal year to date: fiscalYearStart through periodTo. */
  cashFlowYtd: CashFlowResponse;
  /** ISO 8601 datetime the response was computed. */
  generatedAt: string;
}

// ── Tax payment posting ──────────────────────────────────────────────────────
//
// The inventory app also prepares the two Kansas returns: the monthly liquor
// drink tax and the quarterly retail sales tax. The daily sales posting above
// credits what was collected to the two tax payable accounts. When the owner
// marks a return as filed he confirms the withdrawal date, the bank account
// and the amount, and the payment lands here as one journal entry dated the
// withdrawal date: debit the tax payable, credit the bank account's ledger
// account. The withdrawal, when it reaches the bank feed, is then matched to
// this entry instead of being categorised a second time. One posting per
// return, keyed by sourceRef, replaced by void and create like the postings
// above.

/** Every tax payment sourceRef starts with this, which is how the other listings tell them apart. */
export const TAX_PAYMENT_SOURCE_REF_PREFIX = 'inventory-app:tax:';

/** Money string with exactly two decimals that cannot be negative, for example "1234.56". */
/** A function, so each field gets its own instance and the route's JSON schema carries no $ref. */
const moneyNonNegative = () =>
  z.string().regex(/^\d+\.\d{2}$/, 'must be a money string with exactly two decimals, not negative');

/**
 * A two-decimal money string as whole cents, so no float is involved. Null for
 * anything else: the refinement below still runs on a body whose amount has
 * already failed its own check, and must not throw on it.
 */
function cents(value: string): bigint | null {
  return /^\d+\.\d{2}$/.test(value) ? BigInt(value.replace('.', '')) : null;
}

export const taxPaymentPostSchema = z
  .object({
    /**
     * The caller's stable key for the return, for example
     * "inventory-app:tax:drink:2026-08" or "inventory-app:tax:sales:2026-Q3".
     * Repeating a post with the same sourceRef updates the earlier entry
     * instead of adding a second one.
     */
    sourceRef: z
      .string()
      .min(1)
      .max(120)
      .refine((v) => v.startsWith(TAX_PAYMENT_SOURCE_REF_PREFIX), {
        message: `must start with ${TAX_PAYMENT_SOURCE_REF_PREFIX}`,
      }),
    /** Which return is being paid, and so which payable account is debited. */
    tax: z.enum(['drink', 'sales']),
    /** The day the money leaves the bank, YYYY-MM-DD. May be in the future. The entry carries this date. */
    date: CALENDAR_DATE,
    /** The whole amount drawn from the bank. Zero removes any earlier entry for the return and posts nothing. */
    amount: moneyNonNegative(),
    /**
     * The part of `amount` that is penalty and interest rather than tax. It is
     * debited to the penalties account when one is configured, otherwise it
     * stays with the tax on the payable account.
     */
    penaltyAndInterest: moneyNonNegative().default('0.00'),
    /** The bank account (not the ledger account) the payment is drawn from. */
    bankAccountId: z.string().uuid(),
    memo: z.string().max(200).optional(),
  })
  .refine(
    (b) => {
      const amount = cents(b.amount);
      const penalties = cents(b.penaltyAndInterest);
      return amount === null || penalties === null || penalties <= amount;
    },
    {
      message: 'penaltyAndInterest must not be more than amount',
      path: ['penaltyAndInterest'],
    },
  );
export type TaxPaymentPostInput = z.input<typeof taxPaymentPostSchema>;

/** Same meanings as LiquorCogsPostAction. */
export type TaxPaymentPostAction = LiquorCogsPostAction;

export interface TaxPaymentPostResponse {
  sourceRef: string;
  /** YYYY-MM-DD, as posted. */
  date: string;
  /** Two decimals, as posted. */
  amount: string;
  /** The live journal entry for this sourceRef after the call, or null when there is none. */
  entryId: string | null;
  /** That entry's number, for example "JE-01700", or null when there is none. */
  entryNumber: string | null;
  /** The entry that was voided by this call, or null when none was. */
  previousEntryId: string | null;
  action: TaxPaymentPostAction;
}

/** One bank account a tax payment can be drawn from. Nothing secret is included. */
export interface ExportBankAccount {
  id: string;
  name: string;
  /** Last digits of the account number, when known. */
  mask: string | null;
  institutionName: string | null;
  /** The ledger account the payment credits. */
  glAccountId: string;
  glAccountCode: string;
  glAccountName: string;
}

export interface ExportBankAccountsResponse {
  /** Active depository accounts, by name. */
  bankAccounts: ExportBankAccount[];
}
